import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn } from "@/components/Reveal";

const steps = [
  {
    n: "01",
    title: "Watch for my reply",
    text: "I personally review every request and respond within one to two business days, using your preferred contact method.",
  },
  {
    n: "02",
    title: "We find a time",
    text: "We'll schedule your free 20-minute consultation — by phone or secure video, whatever feels easiest.",
  },
  {
    n: "03",
    title: "No pressure",
    text: "The consultation is simply a conversation. If it doesn't feel like the right fit, you've lost nothing but twenty minutes.",
  },
];

const BookingConfirmed = () => {
  usePageMeta(
    "Request Received | Holistic Healing Psychotherapy, Inc.",
    "Your consultation request has been received. You'll hear back within one to two business days."
  );

  useEffect(() => {
    const tag = document.createElement("meta");
    tag.setAttribute("name", "robots");
    tag.setAttribute("content", "noindex");
    document.head.appendChild(tag);
    return () => document.head.removeChild(tag);
  }, []);

  return (
    <div data-testid="booking-confirmed-page">
      <section className="bg-cream-50 py-20 lg:py-28 min-h-[70vh]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <FadeIn>
            <p className="eyebrow mb-6" data-testid="booking-confirmed-eyebrow">Request Received</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-ink" data-testid="booking-confirmed-headline">
              Thank you for <em className="text-mauve-500">reaching out</em>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed max-w-xl mx-auto" data-testid="booking-confirmed-copy">
              Your consultation request has been received. Taking this step isn't always easy — I'm glad you did.
            </p>
          </FadeIn>

          <div className="mt-14 text-left">
            {steps.map((s, i) => (
              <FadeIn key={s.n} delay={i * 0.08}>
                <div className="flex gap-6 py-6 border-t border-forest-800/10" data-testid={`booking-confirmed-step-${s.n}`}>
                  <span className="mt-2 w-2.5 h-2.5 rotate-45 bg-gold-500/70 shrink-0" aria-hidden="true" />
                  <div>
                    <h3 className="font-serif text-xl sm:text-2xl text-forest-800 mb-1.5">{s.title}</h3>
                    <p className="text-base text-ink/70 leading-relaxed">{s.text}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.25}>
            <div className="mt-12">
              <Link
                to="/"
                data-testid="booking-confirmed-home-link"
                className="inline-flex items-center gap-2 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-forest-700"
              >
                Back to Home <ArrowRight size={16} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default BookingConfirmed;
